/* eslint-disable react/no-unstable-nested-components */
import {
    Card,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import ListState from "../../../../components/ListState/ListState";
import { useDebounce } from "../../../../hooks/helpers";
import useToast from "../../../../hooks/useToast";
import Loader from "../../../../shared-ui/Loader/Loader";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import {
    appendMidnightTimeToDate,
    cn,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
} from "../../../../utils/helpers";
import { useUpdateCollateralConsentStatus } from "../../hooks/mutations/reports.mutations";
import { useGetCollateralConsentReports } from "../../hooks/queries/reports.queries";
import {
    ConsentStatus,
    ICollateralConsentReport,
} from "../../types/reports.types";
import {
    ConsentStatusBadge,
    getConsentStatusDisplay,
    getConsentStatusTag,
} from "../../helpers/reports.helper";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import { UserPermisions } from "../../../../utils/types/user";

const FILTER_STORAGE_KEY = "attendeeconsentreports_filters";

function formatNullableDate(date?: string | null) {
    if (!date) return "--";
    return formatDate(appendMidnightTimeToDate(date), true);
}

export default function AttendeeConsentReportsList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { toast } = useToast();
    const updateStatusMutation = useUpdateCollateralConsentStatus();

    const canUpdateConsentStatus = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_SESSION_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const searchString = searchParams.get("search") || "";
    const statusFilter = searchParams.get("status") || "";
    const startDateFromFilter = searchParams.get("start_date_from");
    const startDateToFilter = searchParams.get("start_date_to");
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const offsetFilter = (pageFilter - 1) * limitFilter;
    const search = useDebounce(searchString, 500);

    const { data, error, isLoading, isSuccess } =
        useGetCollateralConsentReports({
            search_string: search,
            status: statusFilter as ConsentStatus,
            start_date_from: startDateFromFilter || undefined,
            start_date_to: startDateToFilter || undefined,
            limit: limitFilter,
            offset: offsetFilter,
        });

    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextParams = new URLSearchParams(searchParams);
        if (e.target.value === "") nextParams.delete("search");
        else nextParams.set("search", e.target.value);
        nextParams.set("page", "1");
        localStorage.setItem(FILTER_STORAGE_KEY, nextParams.toString());
        setSearchParams(nextParams);
    };

    const handleSetStatusFilter = (status: string) => {
        const nextParams = new URLSearchParams(searchParams);
        if (status === "") nextParams.delete("status");
        else nextParams.set("status", status);
        nextParams.set("page", "1");
        localStorage.setItem(FILTER_STORAGE_KEY, nextParams.toString());
        setSearchParams(nextParams);
    };

    const handleSetDateFilter = (key: string, date: Date | null) => {
        const nextParams = new URLSearchParams(searchParams);
        if (date === null) nextParams.delete(key);
        else nextParams.set(key, format(date, "yyyy-MM-dd"));
        nextParams.set("page", "1");
        localStorage.setItem(FILTER_STORAGE_KEY, nextParams.toString());
        setSearchParams(nextParams);
    };

    const handlePageChange = (page: number) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("page", String(page));
        localStorage.setItem(FILTER_STORAGE_KEY, nextParams.toString());
        setSearchParams(nextParams);
    };

    const handleLimitChange = (limit: number) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("limit", String(limit));
        nextParams.set("page", "1");
        localStorage.setItem(FILTER_STORAGE_KEY, nextParams.toString());
        setSearchParams(nextParams);
    };

    const handleChangeConsentStatus = React.useCallback(
        (collateralConsentId: string, status: ConsentStatus) => {
            updateStatusMutation.mutate(
                {
                    collateralConsentId,
                    data: { status },
                },
                {
                    onSuccess: (response) => {
                        toast({
                            mode: "success",
                            message:
                                response.message ||
                                "Attendee consent status updated",
                        });
                    },
                    onError: (err) => {
                        toast({
                            mode: "error",
                            message:
                                err.response?.data.message ||
                                "Could not update attendee consent status",
                        });
                    },
                }
            );
        },
        [toast, updateStatusMutation]
    );

    const columns: ColumnDef<ICollateralConsentReport>[] = React.useMemo(
        () => [
            {
                accessorKey: "attendee_name",
                header: "Attendee Name",
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        <span className="font-medium">
                            {row.original.attendee_name || "--"}
                        </span>
                        {row.original.relationship ? (
                            <span className="text-sm text-gray-500">
                                {row.original.relationship}
                            </span>
                        ) : null}
                    </div>
                ),
            },

            {
                accessorKey: "client_name",
                header: "Client Name",
                cell: ({ row }) => (
                    <Link
                        to={`/clients/${row.original.client_id}`}
                        className="font-medium text-textLink underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {displayNameInRightFormat({
                            firstName: row.original.session_client.first_name,
                            lastName: row.original.session_client.last_name,
                        })}
                    </Link>
                ),
            },
            {
                accessorKey: "email",
                header: "Attendee Email",
                cell: ({ row }) => row.original.email || "--",
            },
            {
                accessorKey: "status",
                header: "Consent Status",
                cell: ({ row }) =>
                    canUpdateConsentStatus ? (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton
                                    className={cn(
                                        `capitalize !min-h-7 ![border:none] px-2 ${getConsentStatusTag(
                                            row.original.status
                                        )}`
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {getConsentStatusDisplay(
                                        row.original.status
                                    )}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width="auto" align="end">
                                {Object.values(ConsentStatus)
                                    .filter(
                                        (status) =>
                                            status !== row.original.status
                                    )
                                    .map((status) => (
                                        <DropdownItem
                                            key={status}
                                            onClick={(e) => {
                                                handleChangeConsentStatus(
                                                    row.original
                                                        .collateral_consent_id,
                                                    status
                                                );
                                                e.stopPropagation();
                                            }}
                                        >
                                            {getConsentStatusDisplay(status)}
                                        </DropdownItem>
                                    ))}
                            </DropdownContent>
                        </Dropdown>
                    ) : (
                        <ConsentStatusBadge status={row.original.status} />
                    ),
            },
            {
                accessorKey: "start_date",
                header: "Session Start Date",
                cell: ({ row }) => formatNullableDate(row.original.start_date),
            },
            {
                accessorKey: "sent_at",
                header: "Date Sent",
                cell: ({ row }) => formatNullableDate(row.original.sent_at),
            },
            {
                accessorKey: "signed_at",
                header: "Signed Date",
                cell: ({ row }) => formatNullableDate(row.original.signed_at),
            },
            {
                accessorKey: "rechecked_at",
                header: "Rechecked Date",
                cell: ({ row }) =>
                    formatNullableDate(row.original.rechecked_at),
            },
        ],
        [canUpdateConsentStatus, handleChangeConsentStatus]
    );

    const startDateFromFilterToDate =
        convertDateFilterStringToDate(startDateFromFilter);
    const startDateToFilterToDate =
        convertDateFilterStringToDate(startDateToFilter);
    return (
        <div>
            <Card classNames="flex flex-col gap-[--spacing-items] mb-[--spacing-header]">
                <div className="flex justify-between items-center">
                    <SearchInput
                        placeholder="Search by client or attendee name"
                        containerClass="w-[400px]"
                        onChange={handleSetSearchString}
                        defaultValue={searchString}
                    />
                </div>

                <div className="flex items-center gap-[--spacing-items] flex-wrap">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {statusFilter
                                    ? getConsentStatusDisplay(
                                          statusFilter as ConsentStatus
                                      )
                                    : "Status"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width={150}>
                            <DropdownItem
                                onClick={() => handleSetStatusFilter("")}
                                isSelected={!statusFilter}
                                closeMenuOnSelect
                            >
                                All Statuses
                            </DropdownItem>
                            {Object.values(ConsentStatus).map((status) => (
                                <DropdownItem
                                    key={status}
                                    onClick={() =>
                                        handleSetStatusFilter(status)
                                    }
                                    isSelected={statusFilter === status}
                                    closeMenuOnSelect
                                >
                                    {getConsentStatusDisplay(status)}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>

                    <Popover>
                        <PopoverTrigger asChild>
                            <FilterButton>Start Date Range</FilterButton>
                        </PopoverTrigger>
                        <PopoverContent align="end">
                            <div className="mb-1">
                                <DatePicker
                                    label="From"
                                    placeholderText="MM/DD/YYYY"
                                    selected={startDateFromFilterToDate}
                                    onChange={(date) =>
                                        handleSetDateFilter(
                                            "start_date_from",
                                            date
                                        )
                                    }
                                    maxDate={startDateToFilterToDate}
                                    isClearable
                                    isInDialog
                                />
                            </div>
                            <DatePicker
                                label="To"
                                placeholderText="MM/DD/YYYY"
                                selected={startDateToFilterToDate}
                                onChange={(date) =>
                                    handleSetDateFilter("start_date_to", date)
                                }
                                minDate={startDateFromFilterToDate}
                                isClearable
                                isInDialog
                            />
                        </PopoverContent>
                    </Popover>
                </div>

                <TableFiltersDisplay
                    appliedFilters={[
                        {
                            key: "status",
                            values: statusFilter
                                ? [
                                      getConsentStatusDisplay(
                                          statusFilter as ConsentStatus
                                      ),
                                  ]
                                : [],
                        },
                        {
                            key: "start_date_from",
                            label: "Start date from",
                            values: startDateFromFilter || "",
                        },
                        {
                            key: "start_date_to",
                            label: "Start date to",
                            values: startDateToFilter || "",
                        },
                    ]}
                    wrapperClass="mt-[--spacing-items]"
                    localStorageKey={FILTER_STORAGE_KEY}
                />
            </Card>

            {isLoading && <Loader />}

            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        "Cannot display attendee consents at this time"
                    }
                />
            )}

            {isSuccess && data.data.length === 0 && (
                <ListState
                    stateHelperText="No consent records match your current filters. Try adjusting your search criteria."
                    isError={false}
                    emptyMessage="No attendee consent records found"
                    emptyBtnProps={{
                        showButton: false,
                    }}
                />
            )}

            {isSuccess && data.data.length > 0 && (
                <Table
                    columns={columns}
                    data={data.data}
                    hasPagination={data.total_count > limitFilter}
                    showSizeFilter
                    pagination={
                        <Pagination
                            totalCount={data.total_count}
                            totalPages={data.total_pages}
                            currentPage={pageFilter}
                            onPageChange={handlePageChange}
                            limit={limitFilter}
                            onLimitChange={handleLimitChange}
                        />
                    }
                />
            )}
        </div>
    );
}
